import { useState } from "react";
import { Calculator, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface OneRMCalculatorProps {
  weightUnit: 'lbs' | 'kg';
  trigger?: React.ReactNode;
  defaultExercise?: string;
}

// 1RM formulas
const formulas = {
  epley: (weight: number, reps: number) => weight * (1 + reps / 30),
  brzycki: (weight: number, reps: number) => weight * (36 / (37 - reps)),
  lombardi: (weight: number, reps: number) => weight * Math.pow(reps, 0.1),
};

export function OneRMCalculator({ weightUnit, trigger, defaultExercise }: OneRMCalculatorProps) {
  const [weight, setWeight] = useState<string>('');
  const [reps, setReps] = useState<string>('');
  const [lift, setLift] = useState<string>(defaultExercise || 'bench');
  const [result, setResult] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const calculate = () => {
    const w = parseFloat(weight);
    const r = parseInt(reps);
    
    if (isNaN(w) || isNaN(r) || w <= 0 || r <= 0 || r > 30) {
      setResult(null);
      return;
    }

    // Use average of common formulas for more accuracy
    const epley = formulas.epley(w, r);
    const brzycki = formulas.brzycki(w, r);
    const lombardi = formulas.lombardi(w, r);
    
    const average = (epley + brzycki + lombardi) / 3;
    setResult(Math.round(average));
  };

  const reset = () => {
    setWeight('');
    setReps('');
    setResult(null);
  };

  // Calculate percentage chart
  const percentages = [100, 95, 90, 85, 80, 75, 70, 65, 60];
  const repsAtPercentage: Record<number, string> = {
    100: '1',
    95: '2-3',
    90: '4',
    85: '5-6',
    80: '7-8',
    75: '9-10',
    70: '11-12',
    65: '13-15',
    60: '16-20',
  };

  const defaultTrigger = (
    <button className="flex items-center gap-1.5 text-[10px] px-2 py-1 rounded bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
      <Calculator className="w-3 h-3" />
      1RM Calc
    </button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            1RM Calculator
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Lift Selection */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Lift</Label>
            <Select value={lift} onValueChange={setLift}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bench">Bench Press</SelectItem>
                <SelectItem value="squat">Squat</SelectItem>
                <SelectItem value="deadlift">Deadlift</SelectItem>
                <SelectItem value="ohp">Overhead Press</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Weight Input */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Weight Lifted ({weightUnit})</Label>
            <Input
              type="number"
              placeholder={`e.g. ${weightUnit === 'lbs' ? '225' : '100'}`}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="text-lg"
            />
          </div>

          {/* Reps Input */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Reps Performed (1-30)</Label>
            <Input
              type="number"
              placeholder="e.g. 5"
              value={reps}
              onChange={(e) => setReps(e.target.value)}
              min={1}
              max={30}
              className="text-lg"
            />
          </div>

          {/* Calculate Button */}
          <div className="flex gap-2">
            <Button onClick={calculate} className="flex-1">
              Calculate 1RM
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {/* Result */}
          {result !== null && (
            <div className="p-4 rounded-xl bg-primary/10 border border-primary/30 text-center space-y-3">
              <p className="text-sm text-muted-foreground">Estimated One Rep Max</p>
              <p className="text-4xl font-bold font-display text-primary">
                {result}<span className="text-lg font-normal ml-1">{weightUnit}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Based on {weight}{weightUnit} × {reps} reps
              </p>

              {/* Percentage Chart */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs font-medium mb-2 text-left">Training Percentages</p>
                <div className="grid grid-cols-3 gap-1 text-[10px]">
                  <span className="text-muted-foreground text-left">%</span>
                  <span className="text-muted-foreground text-center">Weight</span>
                  <span className="text-muted-foreground text-right">Reps</span>
                  {percentages.map((pct) => (
                    <>
                      <span key={`pct-${pct}`} className={cn(
                        "text-left",
                        pct === 100 && "text-primary font-medium"
                      )}>
                        {pct}%
                      </span>
                      <span key={`weight-${pct}`} className={cn(
                        "text-center font-medium",
                        pct === 100 && "text-primary"
                      )}>
                        {Math.round(result * (pct / 100))}{weightUnit}
                      </span>
                      <span key={`reps-${pct}`} className="text-right text-muted-foreground">
                        {repsAtPercentage[pct]}
                      </span>
                    </>
                  ))}
                </div>
              </div>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground text-center">
            Uses an average of Epley, Brzycki, and Lombardi formulas for accuracy.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}